import React, { useEffect, useState } from 'react';

function generateChallenge() {
  const a = Math.floor(Math.random() * 9) + 1; // 1..9
  const b = Math.floor(Math.random() * 9) + 1; // 1..9
  return { a, b, answer: a + b };
}

export default function DeleteRequestForm() {
  const [identifier, setIdentifier] = useState('');
  const [reason, setReason] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const [challenge, setChallenge] = useState(() => generateChallenge());
  const [challengeInput, setChallengeInput] = useState('');
  const [challengeError, setChallengeError] = useState('');

  useEffect(() => {
    // reset challenge input/error when a new challenge is generated
    setChallengeInput('');
    setChallengeError('');
  }, [challenge]);

  function refreshChallenge() {
    setChallenge(generateChallenge());
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setResult(null);
    setChallengeError('');

    if (!identifier.trim() || !confirmChecked) {
      setResult({ type: 'error', text: 'Please provide identifier and confirm deletion.' });
      return;
    }

    // validate challenge: numeric and equals answer
    const userAnswer = parseInt(challengeInput, 10);
    if (Number.isNaN(userAnswer) || userAnswer !== challenge.answer) {
      setChallengeError('Incorrect answer. Please try again or refresh the challenge.');
      // regenerate to avoid repeated guessing
      setChallenge(generateChallenge());
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('https://retail.infobytes.dev/api/Jap/delete-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim(), reason: reason.trim() })
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setResult({ type: 'success', text: body.message || 'Request received. Check your email for verification.' });
        setIdentifier('');
        setReason('');
        setConfirmChecked(false);
        // regenerate challenge for next use
        setChallenge(generateChallenge());
      } else {
        setResult({ type: 'error', text: body.error || 'Unable to submit request.' });
        // regenerate challenge on failure too
        setChallenge(generateChallenge());
      }
    } catch (err) {
      setResult({ type: 'error', text: 'Network error. Try again later.' });
      setChallenge(generateChallenge());
    } finally {
      setLoading(false);
      setChallengeInput('');
    }
  }

  return (
    <main style={{ padding: 20, maxWidth: 780, margin: '0 auto', fontFamily: 'system-ui, Arial' }}>
      <h1>Request account and data deletion of 108 Japa Counter Mala App</h1>
      <img src="/108_Japa_Counter_Mala_Logo.png" alt="108 Japa Counter Mala App Logo" style={{ width: 120, height: 'auto', marginTop: 12 }} />
      <p style={{ color: '#555' }}>Provide the email or user id associated with your account. We will verify and process the request.</p>

      <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
        <label style={{ display: 'block', marginTop: 12, fontWeight: 600 }}>Phone number</label>
        <input
          value={identifier}
          onChange={e => setIdentifier(e.target.value)}
          required
          style={inputStyle}
          aria-label="Email or User ID"
        />

        <label style={{ display: 'block', marginTop: 12, fontWeight: 600 }}>Reason (optional)</label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          style={inputStyle}
          aria-label="Reason for deletion"
        />

        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'block', fontWeight: 600 }}>Prove you're human</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <div aria-live="polite" style={{ padding: 10, borderRadius: 6, background: '#f5f7fb', border: '1px solid #e0e6f0', minWidth: 140 }}>
              <strong style={{ fontSize: 18 }}>{challenge.a}</strong>
              <span style={{ margin: '0 6px' }}>+</span>
              <strong style={{ fontSize: 18 }}>{challenge.b}</strong>
              <span style={{ margin: '0 6px' }}>=</span>
              <input
                type="text"
                value={challengeInput}
                onChange={e => setChallengeInput(e.target.value)}
                style={{ width: 60, padding: 6, borderRadius: 4, border: '1px solid #cfcfcf' }}
                aria-label={`Answer for ${challenge.a} plus ${challenge.b}`}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>

            <button type="button" onClick={refreshChallenge} style={smallButtonStyle} aria-label="Refresh challenge">
              Refresh challenge
            </button>
          </div>
          {challengeError && <div style={{ color: '#8b1e1e', marginTop: 8 }}>{challengeError}</div>}
          <div style={{ color: '#666', fontSize: 13, marginTop: 8 }}>Enter the sum of the two single-digit numbers above.</div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <input type="checkbox" checked={confirmChecked} onChange={e => setConfirmChecked(e.target.checked)} />
          <span>I confirm I want my account and associated data deleted.</span>
        </label>

        <button type="submit" disabled={loading} style={buttonStyle}>
          {loading ? 'Submitting...' : 'Submit deletion request'}
        </button>
      </form>

      {result && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 6, background: result.type === 'success' ? '#e6ffed' : '#fff0f0', border: result.type === 'success' ? '1px solid #b9f0c8' : '1px solid #f2c2c2', color: result.type === 'success' ? '#046a2f' : '#8b1e1e' }}>
          {result.text}
        </div>
      )}
    </main>
  );
}

const inputStyle = {
  width: '100%', padding: 10, borderRadius: 6, border: '1px solid #cfcfcf', boxSizing: 'border-box', marginTop: 6
};

const buttonStyle = {
  marginTop: 12, padding: '10px 14px', background: '#0b69ff', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer'
};

const smallButtonStyle = {
  padding: '8px 10px', borderRadius: 6, border: '1px solid #cfcfcf', background: '#fff', cursor: 'pointer'
};
